import { useEffect, useRef, type RefObject } from "react";
import { createLiftDrag, type PressPoint } from "@/lib/long-press";

/** Within this of an edge, the list (or a column) scrolls under the dragged item. */
const EDGE_PX = 56;
const MAX_SCROLL_STEP = 18;

export interface TouchLiftDragConfig<T> {
  /** What a finger can lift: the closest match to the touch, and its id. */
  itemSelector: string;
  idOf: (item: HTMLElement) => string | undefined;
  /** The lift is the drag (cards); off, a lifted item drags on a move and opens `onRelease` on a still release. */
  dragOnLift: boolean;
  /** What is under the finger for the item being dragged, or null. */
  resolveTarget: (x: number, y: number, id: string) => T | null;
  /** Two targets are the same slot, so `onOver` fires only on a change. */
  sameTarget: (a: T | null, b: T | null) => boolean;
  /** What to scroll near its edges while dragging: sideways, and up and down. */
  scrollers: (x: number, y: number) => { horizontal?: HTMLElement | null; vertical?: HTMLElement | null };
  /** The floating copy's tilt. */
  ghostTransform?: string;
  /** The item rose under a held finger (the row's "lifted" look). */
  onLift?: (id: string) => void;
  onStart: (id: string) => void;
  onOver: (target: T | null) => void;
  onDrop: (id: string, target: T) => void;
  /** Let go over nothing, or the gesture was interrupted. Also ends a lift that came to nothing. */
  onCancel: () => void;
  /** Lifted and let go without moving: the item's menu, at the finger. */
  onRelease?: (id: string, point: PressPoint) => void;
}

/**
 * Drag-and-drop for fingers, for any list: native HTML5 drag never fires for
 * touch. The gesture is `createLiftDrag` (`lib/long-press.ts`, unit-tested): hold
 * to lift — a finger that moves first is scrolling and is left alone, so a list
 * stays swipeable — then the item follows the finger as a floating copy while the
 * original dims in place, and whatever the finger is near the edge of scrolls, so
 * an item can reach a target off screen. What counts as an item, a target and a
 * scroller is the caller's; the board's cards (`useTouchDrag`) and the Agents list
 * both run on this.
 *
 * Listeners are attached by hand because `touchmove` must be non-passive to stop
 * the page scrolling under a drag, and React's are passive.
 */
export function useTouchLiftDrag<T>(container: RefObject<HTMLElement | null>, enabled: boolean, config: TouchLiftDragConfig<T>): void {
  const latest = useRef(config);
  latest.current = config;

  useEffect(() => {
    const root = container.current;
    if (!root || !enabled) return;

    let frame = 0;
    let state: {
      id: string;
      item: HTMLElement;
      x: number;
      y: number;
      ghost: HTMLElement | null;
      offsetX: number;
      offsetY: number;
      target: T | null;
    } | null = null;

    const hitTest = () => {
      if (!state) return;
      const target = latest.current.resolveTarget(state.x, state.y, state.id);
      if (!latest.current.sameTarget(state.target, target)) {
        state.target = target;
        latest.current.onOver(target);
      }
    };

    /** Scrolls whatever the finger is near the edge of, then re-aims. Runs every frame of a drag. */
    const autoScroll = () => {
      if (!state?.ghost) return;
      const speed = (distance: number) => Math.ceil(((EDGE_PX - distance) / EDGE_PX) * MAX_SCROLL_STEP);
      const { horizontal, vertical } = latest.current.scrollers(state.x, state.y);
      let moved = false;
      if (horizontal) {
        const box = horizontal.getBoundingClientRect();
        if (state.x < box.left + EDGE_PX && horizontal.scrollLeft > 0) {
          horizontal.scrollLeft -= speed(state.x - box.left);
          moved = true;
        } else if (state.x > box.right - EDGE_PX && horizontal.scrollLeft < horizontal.scrollWidth - horizontal.clientWidth) {
          horizontal.scrollLeft += speed(box.right - state.x);
          moved = true;
        }
      }
      if (vertical) {
        const rect = vertical.getBoundingClientRect();
        if (state.y < rect.top + EDGE_PX && vertical.scrollTop > 0) {
          vertical.scrollTop -= speed(state.y - rect.top);
          moved = true;
        } else if (state.y > rect.bottom - EDGE_PX && vertical.scrollTop < vertical.scrollHeight - vertical.clientHeight) {
          vertical.scrollTop += speed(rect.bottom - state.y);
          moved = true;
        }
      }
      if (moved) hitTest();
      frame = requestAnimationFrame(autoScroll);
    };

    const startDrag = () => {
      if (!state) return;
      const rect = state.item.getBoundingClientRect();
      state.offsetX = state.x - rect.left;
      state.offsetY = state.y - rect.top;
      const ghost = state.item.cloneNode(true) as HTMLElement;
      Object.assign(ghost.style, {
        position: "fixed",
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        margin: "0",
        pointerEvents: "none",
        zIndex: "60",
        transform: latest.current.ghostTransform ?? "rotate(1.5deg) scale(1.03)",
        boxShadow: "0 18px 40px -12px rgba(0,0,0,0.55)",
        opacity: "0.96",
        transition: "transform 120ms ease-out",
      });
      document.body.appendChild(ghost);
      state.ghost = ghost;
      // Snap is for flicking between columns; it would fight the edge scroll.
      root.style.scrollSnapType = "none";
      latest.current.onStart(state.id);
      hitTest();
      frame = requestAnimationFrame(autoScroll);
    };

    const machine = createLiftDrag({
      dragOnLift: latest.current.dragOnLift,
      schedule: (fire, delay) => {
        const handle = setTimeout(fire, delay);
        return () => clearTimeout(handle);
      },
      buzz: () => navigator.vibrate?.(12),
      onLift: () => {
        if (state) latest.current.onLift?.(state.id);
      },
      onDragStart: startDrag,
      onRelease: (point) => {
        const id = state?.id;
        cleanup();
        latest.current.onCancel();
        if (id) latest.current.onRelease?.(id, point);
      },
      onDrop: () => {
        const finished = state;
        cleanup();
        if (finished?.target) latest.current.onDrop(finished.id, finished.target);
        else latest.current.onCancel();
      },
      onCancel: () => {
        cleanup();
        latest.current.onCancel();
      },
    });

    const cleanup = () => {
      cancelAnimationFrame(frame);
      state?.ghost?.remove();
      root.style.scrollSnapType = "";
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onCancel);
      state = null;
    };

    const onMove = (event: TouchEvent) => {
      if (!state) return;
      const touch = event.touches[0];
      if (!touch) return;
      state.x = touch.clientX;
      state.y = touch.clientY;
      const owned = machine.move({ x: state.x, y: state.y });
      if (machine.phase === "idle") {
        cleanup(); // a scroll: the list is the finger's
        return;
      }
      if (!owned) return;
      event.preventDefault();
      if (state?.ghost) {
        state.ghost.style.left = `${state.x - state.offsetX}px`;
        state.ghost.style.top = `${state.y - state.offsetY}px`;
        hitTest();
      }
    };

    const onEnd = (event: TouchEvent) => {
      if (!state) return;
      const point = { x: state.x, y: state.y };
      if (machine.phase === "pending") {
        machine.up(point);
        cleanup(); // a tap: the item's own click acts
        return;
      }
      event.preventDefault();
      // The browser may still send a click for this touch; it must not also act as a tap.
      const swallow = (click: Event) => {
        click.stopPropagation();
        click.preventDefault();
      };
      window.addEventListener("click", swallow, { capture: true, once: true });
      setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 400);
      machine.up(point);
      cleanup();
    };

    const onCancel = () => {
      machine.cancel();
      cleanup();
    };

    const onStart = (event: TouchEvent) => {
      if (event.touches.length !== 1 || state) return;
      // A control with a hold of its own inside the item (the needs-you pill) keeps its press.
      if ((event.target as HTMLElement | null)?.closest("[data-own-hold]")) return;
      const item = (event.target as HTMLElement | null)?.closest<HTMLElement>(latest.current.itemSelector);
      const id = item ? latest.current.idOf(item) : undefined;
      if (!item || !id || !root.contains(item)) return;
      const touch = event.touches[0]!;
      state = { id, item, x: touch.clientX, y: touch.clientY, ghost: null, offsetX: 0, offsetY: 0, target: null };
      document.addEventListener("touchmove", onMove, { passive: false });
      document.addEventListener("touchend", onEnd, { passive: false });
      document.addEventListener("touchcancel", onCancel);
      machine.down({ x: touch.clientX, y: touch.clientY });
    };

    // A long press would otherwise open the browser's own menu over the item.
    const onContextMenu = (event: Event) => {
      if ((event.target as HTMLElement | null)?.closest(latest.current.itemSelector)) event.preventDefault();
    };

    root.addEventListener("touchstart", onStart, { passive: true });
    root.addEventListener("contextmenu", onContextMenu);
    return () => {
      root.removeEventListener("touchstart", onStart);
      root.removeEventListener("contextmenu", onContextMenu);
      machine.cancel();
      cleanup();
    };
  }, [container, enabled]);
}

/* ------------------------------------------------------- the board's cards */

export interface TouchDragHandlers {
  /** A card was lifted. */
  onStart: (taskId: string) => void;
  /** The card is over a column at this slot, or over nothing. */
  onOver: (target: { columnId: string; index: number } | null) => void;
  /** Let go over a column. */
  onDrop: (taskId: string, columnId: string, index: number) => void;
  /** Let go over nothing, or the gesture was interrupted. */
  onCancel: () => void;
}

type CardSlot = { columnId: string; index: number };

/**
 * The column list under a point, only if it belongs to this board: with two board
 * windows side by side, a card dragged out of one must not land in the other's column.
 */
const columnListAt = (board: HTMLElement | null, x: number, y: number): HTMLElement | null => {
  const list = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-column-list]") ?? null;
  return list && board?.contains(list) ? list : null;
};

/**
 * A board's cards, dragged by a finger. It reuses the board's own drop model — the
 * column under the finger, and the slot from the finger's position against each
 * card's midpoint, exactly what `board-column.tsx` computes for a mouse — so a card
 * lands where it would have with a mouse. Near the left or right edge the board
 * scrolls sideways, near the top or bottom of a column the column scrolls, so a
 * card can reach a column that is off screen on a phone.
 */
export function useTouchDrag(container: RefObject<HTMLElement | null>, enabled: boolean, handlers: TouchDragHandlers): void {
  const latest = useRef(handlers);
  latest.current = handlers;
  useTouchLiftDrag<CardSlot>(container, enabled, {
    itemSelector: "[data-task-id]",
    idOf: (card) => card.dataset.taskId,
    dragOnLift: true,
    resolveTarget: (x, y) => {
      const list = columnListAt(container.current, x, y);
      const columnId = list?.dataset.columnList;
      if (!list || !columnId) return null;
      const cards = Array.from(list.querySelectorAll<HTMLElement>("[data-card]"));
      let index = cards.length;
      for (let position = 0; position < cards.length; position += 1) {
        const rect = cards[position]!.getBoundingClientRect();
        if (y < rect.top + rect.height / 2) {
          index = position;
          break;
        }
      }
      return { columnId, index };
    },
    sameTarget: (a, b) => a?.columnId === b?.columnId && a?.index === b?.index,
    scrollers: (x, y) => ({
      horizontal: container.current,
      vertical: columnListAt(container.current, x, y),
    }),
    onStart: (taskId) => latest.current.onStart(taskId),
    onOver: (target) => latest.current.onOver(target),
    onDrop: (taskId, target) => latest.current.onDrop(taskId, target.columnId, target.index),
    onCancel: () => latest.current.onCancel(),
  });
}
